import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { DeviceMobile, CheckCircle, ShieldCheck, Table, DownloadSimple } from "@phosphor-icons/react";

const STATUS_LABEL = {
  enabled: { t: "Enabled", c: "bg-[#DCFCE7]" },
  opted_out: { t: "Opted out", c: "bg-[#FEE2E2]" },
  pending_verification: { t: "Pending verification", c: "bg-[#FEF9C3]" },
  not_enrolled: { t: "Not enabled", c: "bg-[#F3F4F6]" },
};

export default function SmsNotifications() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [info, setInfo] = useState(null);
  const [phone, setPhone] = useState("");
  const [step, setStep] = useState("idle"); // idle | code | confirm
  const [code, setCode] = useState("");
  const [demoNote, setDemoNote] = useState(null);
  const [busy, setBusy] = useState(false);
  const [records, setRecords] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/sms/consent/status"); setInfo(data); setPhone(data.phone || ""); }
    catch (e) { /* not logged in edge */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const startConsent = () => {
    if (!phone.trim()) { toast.error("Enter your mobile number"); return; }
    setStep("confirm");
  };

  const sendCode = async () => {
    if (!phone.trim()) { toast.error("Enter your mobile number"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/sms/consent/verify/start", { phone: phone.trim() });
      setStep("code");
      if (data.demo_code) { setDemoNote(data); setCode(data.demo_code); }
      toast.success(data.is_test ? "Test mode — verification code shown below (no real text sent)" : "Verification code sent to your phone");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const verify = async () => {
    setBusy(true);
    try { await api.post("/sms/consent/verify/check", { code: code.trim() }); setStep("confirm"); toast.success("Number verified"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const agreeEnable = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/sms/consent/enable", { phone: phone.trim(), agreed: true });
      toast.success("SMS notifications enabled");
      setStep("idle"); setDemoNote(null); setCode("");
      await load();
      if (data.is_test) toast.message("Test - No SMS Sent", { description: "Consent recorded. A confirmation text will send once Twilio is live." });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const cancelFlow = () => { setStep("idle"); setCode(""); setDemoNote(null); };

  const disable = async () => {
    if (!window.confirm("You will no longer receive work-related SMS notifications. You may continue using Short Stack Crew normally. Disable SMS?")) return;
    setBusy(true);
    try { await api.post("/sms/consent/disable"); toast.success("SMS notifications disabled"); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const loadRecords = async () => {
    try { const { data } = await api.get("/sms/consent/records"); setRecords(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const exportCsv = async () => {
    try {
      const res = await api.get("/sms/consent/records/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "sms_consent_records.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Export failed"); }
  };

  if (!info) return null;
  const st = STATUS_LABEL[info.status] || STATUS_LABEL.not_enrolled;
  const enabled = info.status === "enabled";

  return (
    <div>
      <p className="text-xs font-bold uppercase tracking-[0.15em] text-black/40 mb-2">Notifications › SMS Notifications</p>

      <div className="p-4 rounded-xl border-2 border-black bg-white space-y-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 font-bold text-sm"><DeviceMobile size={18} weight="bold" /> SMS Notifications</div>
          <span data-testid="sms-status-badge" className={`text-[11px] font-bold px-2 py-1 rounded border-2 border-black ${st.c}`}>{st.t}{info.is_test && enabled ? " · Test" : ""}</span>
        </div>

        <p className="text-xs text-black/55">You may receive: {info.message_types?.join(", ")}.</p>

        <div className="text-[11px] leading-relaxed text-black/60 border-t border-black/10 pt-3" data-testid="sms-disclosure">{info.disclosure}</div>
        <div className="flex flex-wrap gap-3 text-[11px] font-semibold">
          <a href="/sms-consent" target="_blank" rel="noopener noreferrer" className="underline flex items-center gap-1"><ShieldCheck size={12} weight="bold" /> SMS program details</a>
          <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline">Privacy Policy</a>
          <a href="/terms" target="_blank" rel="noopener noreferrer" className="underline">Terms &amp; Conditions</a>
        </div>

        {enabled ? (
          <div className="border-t border-black/10 pt-3 space-y-2">
            <p className="text-xs text-black/60 flex items-center gap-1.5"><CheckCircle size={14} weight="bold" className="text-green-700" /> Active on the number ending in {info.phone_last4}.</p>
            <button data-testid="sms-disable-btn" onClick={disable} disabled={busy} className="press h-10 px-4 rounded-full border-2 border-black bg-white font-bold text-sm text-red-600 disabled:opacity-50">Disable SMS Notifications</button>
          </div>
        ) : (
          <div className="border-t border-black/10 pt-3 space-y-3">
            {step === "idle" && (
              <>
                <label className="text-xs font-bold uppercase tracking-[0.12em] text-black/60 block">Your mobile number</label>
                <input data-testid="sms-phone-input" type="tel" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                  placeholder="+1 808 555 1234" className="w-full h-11 px-3 rounded-lg border-2 border-black bg-white outline-none focus:ring-2 focus:ring-primary text-sm" />
                <div className="flex flex-wrap gap-2">
                  <button data-testid="sms-enable-btn" onClick={startConsent} disabled={busy} className="press h-11 px-4 rounded-full border-2 border-black bg-primary text-white font-bold text-sm disabled:opacity-50">Enable SMS Notifications</button>
                  <button data-testid="sms-notnow-btn" onClick={() => toast.message("No problem — SMS stays off. You can enable it anytime.")} className="press h-11 px-4 rounded-full border-2 border-black bg-white font-bold text-sm">Not Now</button>
                </div>
                <button data-testid="sms-verify-link" onClick={sendCode} disabled={busy} className="text-[11px] underline text-black/50">Verify my number first (recommended)</button>
                <p className="text-[11px] text-black/40">Enabling is optional and is not a condition of employment or app use.</p>
              </>
            )}

            {step === "code" && (
              <>
                {demoNote && <div className="text-[11px] rounded-lg border-2 border-black bg-[#FEF9C3] p-2" data-testid="sms-test-note"><strong>Test - No SMS Sent.</strong> For testing, your verification code is <strong>{demoNote.demo_code}</strong> (once Twilio is live this arrives by text to •••• {demoNote.phone_last4}).</div>}
                <label className="text-xs font-bold uppercase tracking-[0.12em] text-black/60 block">Enter verification code</label>
                <input data-testid="sms-code-input" inputMode="numeric" value={code} onChange={(e) => setCode(e.target.value)} placeholder="6-digit code"
                  className="w-full h-11 px-3 rounded-lg border-2 border-black bg-white outline-none focus:ring-2 focus:ring-primary text-sm" />
                <div className="flex flex-wrap gap-2">
                  <button data-testid="sms-verify-btn" onClick={verify} disabled={busy} className="press h-11 px-4 rounded-full border-2 border-black bg-primary text-white font-bold text-sm disabled:opacity-50">Verify number</button>
                  <button onClick={cancelFlow} className="press h-11 px-4 rounded-full border-2 border-black bg-white font-bold text-sm">Not Now</button>
                </div>
              </>
            )}

            {step === "confirm" && (
              <>
                <div className="text-[11px] leading-relaxed text-black border-2 border-black rounded-lg p-3 bg-[#F3E8FF]" data-testid="sms-final-disclosure">{info.disclosure}</div>
                <div className="flex flex-wrap gap-2">
                  <button data-testid="sms-agree-btn" onClick={agreeEnable} disabled={busy} className="press h-11 px-4 rounded-full border-2 border-black bg-primary text-white font-bold text-sm disabled:opacity-50">I Agree - Enable SMS Notifications</button>
                  <button onClick={cancelFlow} className="press h-11 px-4 rounded-full border-2 border-black bg-white font-bold text-sm">Not Now</button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {isOwner && (
        <div className="mt-3">
          {!records ? (
            <button data-testid="sms-records-btn" onClick={loadRecords} className="press h-10 px-4 rounded-full border-2 border-black bg-white font-bold text-sm flex items-center gap-2"><Table size={15} weight="bold" /> View SMS Consent Records</button>
          ) : (
            <div className="p-4 rounded-xl border-2 border-black bg-white">
              <div className="flex items-center justify-between mb-2">
                <p className="font-bold text-sm">SMS Consent Records ({records.length})</p>
                <button data-testid="sms-export-btn" onClick={exportCsv} className="press text-xs font-bold flex items-center gap-1"><DownloadSimple size={13} weight="bold" /> Export CSV</button>
              </div>
              {records.length === 0 ? <p className="text-xs text-black/40">No consent records yet.</p> : (
                <div className="max-h-64 overflow-y-auto text-[11px]">
                  {records.map((r) => (
                    <div key={r.id} className="flex items-center justify-between gap-2 py-1.5 border-b border-black/10">
                      <div className="min-w-0">
                        <span className="font-bold">{r.employee_name || "—"}</span> <span className="text-black/40">{r.phone_masked || ""}</span>
                        {r.is_test && <span className="ml-1 text-[9px] font-bold px-1 rounded bg-[#FEF9C3] border border-black">TEST</span>}
                        <div className="text-black/40">{new Date(r.created_at).toLocaleString()} · v{r.consent_language_version}</div>
                      </div>
                      <span className={`shrink-0 text-[9px] font-bold px-1.5 py-0.5 rounded border border-black ${r.status === "enabled" ? "bg-[#DCFCE7]" : "bg-[#FEE2E2]"}`}>{r.status}</span>
                    </div>
                  ))}
                </div>
              )}
              <button onClick={() => setRecords(null)} className="press mt-2 text-xs font-bold text-black/50">Hide</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
